"use client";

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  EcommerceTermsProvider,
  useEcommerceTerms,
} from "@/integrations/wordpress/WordPressEcommerceTermsProvider";
import {
  ShopBreadcrumbsProvider,
  useShopBreadcrumbs,
} from "@/integrations/wordpress/WordPressEcommerceShopBreadcrumbs";
import { WP_Term_Query } from "@/integrations/wordpress/wp_term_query";

function ArchiveBreadcrumbs() {
  const { links, loading, error } = useShopBreadcrumbs();

  if (loading || error || links.length === 0) return null;

  const lastIndex = links.length - 1;

  return (
    <Breadcrumb>
      <BreadcrumbList className="text-muted-foreground font-mono text-xs uppercase tracking-widest">
        {links.map((link, i) => (
          <div
            key={link.href}
            data-index={i}
            className="inline-flex items-center gap-2.5"
          >
            <BreadcrumbItem>
              {i === lastIndex ? (
                <BreadcrumbPage className="text-primary font-medium">
                  {link.title}
                </BreadcrumbPage>
              ) : (
                <BreadcrumbLink
                  href={link.href}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  {link.title}
                </BreadcrumbLink>
              )}
            </BreadcrumbItem>
            {i !== lastIndex && (
              <BreadcrumbSeparator className="text-muted-foreground/50" />
            )}
          </div>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

function ArchiveTitleConsumer() {
  const { terms, loading, error } = useEcommerceTerms();

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-4 w-64 bg-muted/40 animate-pulse" />
        <div className="h-16 w-3/4 bg-muted/40 animate-pulse" />
        <div className="h-4 w-1/2 bg-muted/30 animate-pulse" />
      </div>
    );
  }

  if (error) {
    return (
      <p className="text-sm text-muted-foreground font-mono uppercase tracking-wider">
        Unable to load collection.
      </p>
    );
  }

  const currentTerm = terms && terms.length > 0 ? terms[0] : null;
  const title = currentTerm?.name ?? "Essence Collections";
  const description = currentTerm?.description ?? "";
  const taxonomyLabel =
    currentTerm?.taxonomy === "product_tag" ? "Essence" : "Collection";

  return (
    <div className="relative">
      <div className="mb-10 md:mb-12">
        <ArchiveBreadcrumbs />
      </div>

      <div className="flex items-center gap-3 mb-6">
        <span className="inline-block h-px w-10 bg-primary" />
        <span className="font-mono text-xs uppercase tracking-widest text-primary">
          {taxonomyLabel} / Essence & Co.
        </span>
      </div>

      <h1 className="font-default font-bold text-foreground text-5xl sm:text-6xl md:text-7xl lg:text-8xl tracking-tight leading-[0.95] uppercase break-words">
        {title}
      </h1>

      {description && (
        <p
          className="mt-8 max-w-2xl font-default text-lg md:text-xl text-muted-foreground leading-relaxed"
          dangerouslySetInnerHTML={{ __html: description }}
        />
      )}

      <div className="mt-12 h-px w-full bg-border" />
    </div>
  );
}

export default function ArchiveTitle() {
  const current_wp_term_query_params =
    typeof window !== "undefined" && (window as any).wvcClient
      ? (window as any).wvcClient.getCurrentWPTermQueryParams()
      : {};
  const wp_term_query = new WP_Term_Query(current_wp_term_query_params);

  return (
    <section data-section-id="4218"
      id="archive-title"
      className="relative bg-background text-foreground overflow-hidden"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(var(--foreground) 1px, transparent 1px), linear-gradient(90deg, var(--foreground) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-32 -right-32 h-96 w-96 rounded-lg blur-3xl opacity-20"
        style={{ background: "var(--primary)" }}
      />

      <div className="relative mx-auto max-w-7xl px-6 md:px-10 pt-28 pb-16 md:pt-36 md:pb-24">
        <ShopBreadcrumbsProvider>
          <EcommerceTermsProvider wp_term_query={wp_term_query}>
            <ArchiveTitleConsumer />
          </EcommerceTermsProvider>
        </ShopBreadcrumbsProvider>
      </div>
    </section>
  );
}